import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Crosshair } from 'lucide-react';
import {
  GPS_ACCURACY_MAX_METRES,
  GPS_ACCURACY_MIN_METRES,
  type FieldSettings,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { Button, Card, ErrorNotice, Field, Input, Pill, Spinner } from '../../components/ui';

/**
 * The operator's own policy dial: how accurate a GPS fix has to be before the
 * field app will place equipment on it. Below the threshold a fix is recorded
 * and the job goes incomplete — never rounded up, never placed — so this number
 * is the line between "good enough to trust" and "an administrator has to look".
 *
 * It also reports whether the photo step is live at all, which is NOT something
 * the org sets: photographs go to object storage, and with none configured the
 * step is off rather than quietly degraded onto this server.
 */
export function GpsPolicyCard() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['field', 'settings'],
    queryFn: () => api.get<FieldSettings>('/field/settings'),
  });
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Seed the input from the stored policy once it arrives, and leave the
  // operator's own edits alone afterwards.
  useEffect(() => {
    if (settings.data && value === '') setValue(String(settings.data.gpsAccuracyMeters));
  }, [settings.data, value]);

  const save = useMutation({
    mutationFn: () => api.put<FieldSettings>('/field/settings', { gpsAccuracyMeters: Number(value) }),
    onSuccess: (r) => {
      setError(null);
      setValue(String(r.gpsAccuracyMeters));
      queryClient.setQueryData(['field', 'settings'], r);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not save the policy'),
  });

  const n = Number(value);
  const inRange =
    Number.isInteger(n) && n >= GPS_ACCURACY_MIN_METRES && n <= GPS_ACCURACY_MAX_METRES;
  const dirty = settings.data ? n !== settings.data.gpsAccuracyMeters : false;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Crosshair className="size-4 text-muted" />
          GPS accuracy policy
        </span>
      }
    >
      <div className="space-y-4 p-5">
        {settings.isLoading ? (
          <div className="flex justify-center p-4">
            <Spinner />
          </div>
        ) : settings.isError ? (
          <ErrorNotice error={settings.error as Error} />
        ) : settings.data ? (
          <>
            <p className="text-sm text-muted">
              A fix the field app reports as worse than this is recorded and the job goes
              incomplete — never placed. {GPS_ACCURACY_MIN_METRES}–{GPS_ACCURACY_MAX_METRES} m; a
              phone under clear sky settles at 3–8 m.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-40">
                <Field label="Threshold (metres)">
                  <Input
                    type="number"
                    min={GPS_ACCURACY_MIN_METRES}
                    max={GPS_ACCURACY_MAX_METRES}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="font-mono"
                  />
                </Field>
              </div>
              <Button
                variant="primary"
                disabled={!inRange || !dirty}
                loading={save.isPending}
                onClick={() => save.mutate()}
              >
                Save policy
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {settings.data.isDefault && (
                <Pill tone="neutral">Using the built-in default ({settings.data.gpsAccuracyMeters} m)</Pill>
              )}
              <Pill state tone={settings.data.photosEnabled ? 'success' : 'neutral'}>
                <Camera className="mr-1 inline size-3" />
                Deployment photos {settings.data.photosEnabled ? 'on' : 'off'}
              </Pill>
            </div>
            {error && <ErrorNotice error={error} />}
          </>
        ) : null}
      </div>
    </Card>
  );
}
